import type { AccessRequestNodeFragment } from '@frameio/access-requests/gql/fragments/generated/AccessRequestNode';
import type {
  NotificationLink,
  NotificationLinkAccessRequestFolder,
} from '@frameio/graphql-client/generated';
import { getLink } from '@frameio/shared/routing/getLink';
import { getLinkToProjectWithMemberDialogOpen } from '@frameio/shared/routing/getLinkToProjectWithMembersDialogOpen';

type GetLinkForNotificationCell = {
  link?:
    | ({
        __typename: 'AccessRequest';
      } & Pick<AccessRequestNodeFragment, 'id' | 'requestedResource'>)
    | Partial<NotificationLink>
    | {
        /** Aliased field to prevent type conflict with folder field in `NotificationLinkFolder` type */
        __typename?: 'NotificationLinkAccessRequestFolder';
        accessRequestedResourceFolder: NotificationLinkAccessRequestFolder['folder'];
        project?: NotificationLinkAccessRequestFolder['project'];
      };
};
/**
 * @description The `getLinkForNotificationCell` function gets the full constructed link for a given notification type.
 * @param {NotificationLink} link whether the notification group is read or not.
 * @returns {string} the link to use in the notification cell.
 */
export const getLinkForNotificationCell = ({
  link,
}: GetLinkForNotificationCell) => {
  switch (link?.__typename) {
    case 'NotificationLinkAsset': {
      const assetId = link?.asset?.id;
      const projectId = link?.project?.id;
      const versionId =
        link.__typename === 'NotificationLinkAsset'
          ? link?.version?.id
          : undefined;

      if (!assetId || !projectId) return '#';

      const folderViewerLink = getLink({
        page: 'folderViewer',
        assetId,
        projectId,
        versionId,
      });

      return folderViewerLink;

      break;
    }

    case 'NotificationLinkComment': {
      const assetId = link?.asset?.id;
      const commentId = link?.comment?.id;
      const projectId = link?.project?.id;
      const versionId = link?.version?.id;

      if (!assetId || !projectId) return '#';

      const folderViewerLink = getLink({
        page: 'folderViewer',
        assetId,
        projectId,
        commentId,
        versionId,
      });

      return folderViewerLink;

      break;
    }

    case 'NotificationLinkCommentViaShare': {
      const assetId = link?.asset?.id;
      const shareId = link?.share?.id;

      if (!assetId || !shareId) return '#';

      return getLink({
        page: 'shareReview',
        shareId,
        assetId,
      });

      break;
    }

    case 'NotificationLinkFolder': {
      const folderId = link?.folder?.id;
      const projectId = link?.project?.id;
      if (!projectId) return '#';
      return getLink({
        page: 'folder',
        folderAssetId: folderId,
        projectId,
      });

      break;
    }

    case 'NotificationLinkAccessRequestFolder':
    case 'NotificationLinkAccessRequestCollection':
    case 'NotificationLinkAccessRequestAsset':
    case 'NotificationLinkAccessRequestProject': {
      return getLinkToProjectWithMemberDialogOpen({
        projectId: link?.project?.id ?? '',
      });
      break;
    }

    case 'AccessRequest': {
      const { requestedResource } = link;

      if (!requestedResource) return '#';

      switch (requestedResource.__typename) {
        case 'Project':
          return getLink({
            page: 'project',
            projectId: requestedResource.id,
          });
        case 'FolderAsset':
          return getLink({
            page: 'folder',
            projectId: requestedResource.folderAssetProject?.id ?? '',
            folderAssetId: requestedResource.id ?? '',
          });
        case 'Collection':
          return getLink({
            page: 'collection',
            collectionId: requestedResource.id ?? '',
            projectId: requestedResource.collectionProject?.id ?? '',
          });

        default:
          /** All other types are Asset types with a 'project' field */
          if ('project' in requestedResource) {
            return getLink({
              page: 'folderViewer',
              projectId: requestedResource.project?.id ?? '',
              assetId: requestedResource.id ?? '',
            });
          }
          return '#';
      }

      break;
    }

    default:
      return '#';
      break;
  }
  return '#';
};
